import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getLeavePolicyTemplates,
  getLeavePolicyTemplate,
  createLeavePolicyTemplate,
  updateLeavePolicyTemplate,
  deleteLeavePolicyTemplate,
} from '../../../services/leavePolicyTemplateService';
import { normalizeAccrualFields } from '../../../utils/leaveAccrual';

// Şablonlar global bir liste (tenant'a ait değil, sayfalama/filtre yok) —
// GetAll backend'de tüm aktif şablonları tek seferde döner, parametre almaz
// (bkz. LeavePolicyTemplateDataAccess.GetAll). roles/comp-cycles/calibration-
// sessions ile AYNI desen: DataTable `serverSide={false}` alır, client-side
// filtre/sıralama tam liste üzerinde gerçekten çalışır.
export const useLeavePolicyTemplateApi = () => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    getLeavePolicyTemplates()
      .then((res) => {
        const items = Array.isArray(res.data?.data) ? res.data.data : [];
        setData(items);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  // Düzenleme akışı GetAll'un özet satırından DEĞİL, GetById'den beslenmeli —
  // GetAll projeksiyonu Variations'ı doldurmaz (yalnız VariationCount), bu
  // yüzden satırı doğrudan forma vermek tür listesini sessizce boşaltırdı.
  const getOne = useCallback(async (id) => {
    const res = await getLeavePolicyTemplate(id);
    return res.data?.status ? res.data.data : null;
  }, []);

  // Tür listesindeki nullable sayısal alanlar: boş string'i null'a çevir.
  // Editör yeni bir türü bu alanlar '' olacak şekilde tohumluyor ve kullanıcı
  // girdiği sayıyı silerse yine '' kalıyor. Backend DTO'da bunlar decimal?/int?
  // (LeavePolicyTemplateDtos.cs); ASP.NET Core JsonSerializerDefaults.Web
  // kullandığı için "5" gibi SAYISAL bir string sorunsuz okunur, ama "" okunamaz
  // ve System.Text.Json gövdenin TAMAMINI reddeder ("The JSON value could not be
  // converted to System.Nullable`1[System.Decimal]") → 400. Yani sayısal alanları
  // boş bırakmak — ki varsayılan durum budur — kayıt yolunu tümden çökertiyordu.
  // Aynı temizleyici deseni pages/tenants/config/index.js'te de var; buradaki
  // fark, alanların şablonun kökünde değil tür listesinin İÇİNDE olması.
  //
  // `anchorToleranceDays` (Task 10 — bakiye ekseni alanları) AYNI ailedendir:
  // backend'de int? (LeaveKinds/LeaveAxisValidator), editör onu da '' ile
  // tohumluyor ve alan yalnız eligibilityAnchor==='birthday' iken görünür —
  // yani ÇOĞU tür için gizli kalıp '' olarak gövdeye girecekti. Listeye
  // eklenmezse HER şablon kaydı (doğum günü çapası kullanmayan türler dahil)
  // aynı 400'e çarpardı.
  const VARIATION_NULLABLE_FIELDS = ['instanceLimit', 'monthlyLimit', 'negativeBalanceLimit', 'cooldownDays', 'anchorToleranceDays'];
  const preparePayload = (fd) => {
    const variations = Array.isArray(fd?.variations) ? fd.variations : [];
    return {
      ...fd,
      variations: variations.map((v) => {
        const cleaned = { ...v };
        VARIATION_NULLABLE_FIELDS.forEach((k) => {
          // Yalnız '' ve undefined temizlenir: 0 geçerli bir değerdir, falsy
          // kontrolü kullanılırsa sessizce null'a düşerdi.
          if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null;
        });
        // Hak ediş / devir tavanı alanları: boş → null, yıllık türde aylık-özel ayarlar
        // varsayılana, görünmeyen tavan (devretmeyen / Türkiye kilitli) null'a. Şablon
        // güncellemesi tür listesini YENİDEN yazar — alan hiç gönderilmezse kayıtlı
        // tavan sessizce silinirdi, bu yüzden her tür için AÇIKÇA gönderilir.
        return normalizeAccrualFields(cleaned, fd?.countryCode ?? '');
      }),
    };
  };

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await createLeavePolicyTemplate(preparePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await updateLeavePolicyTemplate(id, preparePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => deleteLeavePolicyTemplate(id)));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('leavePolicyTemplate.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'countryCode', label: t('leavePolicyTemplate.countryCode'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'variationCount', label: t('leavePolicyTemplate.variationCount'), options: { filter: false, sort: false } },
  ], [t]);

  const countryOptions = useMemo(() => {
    try {
      const dn = new Intl.DisplayNames(['en'], { type: 'region' });
      const codes = ['TR', 'US', 'GB', 'DE', 'FR', 'RU', 'KZ', 'AZ', 'NL', 'BE', 'IT', 'ES', 'PT', 'GR', 'PL', 'CZ', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI', 'IE', 'CA', 'AU', 'NZ', 'JP', 'KR', 'CN', 'IN', 'BR', 'MX', 'AR', 'SA', 'AE', 'QA', 'KW', 'EG', 'ZA', 'NG', 'KE', 'UA', 'GE', 'BY', 'BG', 'RO', 'HU', 'SK', 'HR', 'RS', 'BA', 'MK', 'AL', 'XK', 'ME', 'SI', 'LT', 'LV', 'EE', 'IS', 'MT', 'CY', 'LU', 'LI', 'MC', 'AD'];
      return codes.map((c) => ({ id: c, name: `${c} — ${dn.of(c)}` })).sort((a, b) => a.name.localeCompare(b.name));
    } catch { return []; }
  }, []);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('leavePolicyTemplate.name'), type: 'text', grid: 12, required: true },
    {
      name: 'countryCode', label: t('leavePolicyTemplate.countryCode'), type: 'select', grid: 12,
      options: countryOptions,
      idKey: 'id', labelKey: 'name',
    },
    { name: 'description', label: t('leavePolicyTemplate.description'), type: 'textarea', grid: 12 },
    {
      name: 'variations',
      label: t('leavePolicyTemplate.variationCount'),
      type: 'leavePolicyTemplateVariations',
      grid: 12,
      defaultValue: [],
    },
  ], [t, countryOptions]);

  return { objectSchema, columns, data, fetching, getOne, create, update, _delete };
};
